import { useMemo } from "react";
import { endOfDay, startOfDay } from "date-fns";
import { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";
import { useListDriverIncidentsQuery } from "~/shared/api/driver-incidents/driver-incidents.grpc";
import { plannedContainerCount } from "~/shared/lib/job-containers";

// A pickup location can hold several container groups, but a job services
// exactly one of them — only that group counts.
export const getJobVolume = (job: ConnectProto.Job): number => {
  const group = job.containerGroup;
  if (!group) return 0;
  return (group.containerType?.volume ?? 0) * plannedContainerCount(job);
};

// Container types go down to 0.24 m³, so one decimal is too coarse — it would
// round a 4 × 0.24 group up to a whole cubic metre.
export const roundVolume = (volume: number) => Math.round(volume * 100) / 100;

// A job may carry only one of the two bounds; treat the present one as the
// whole window. No bounds at all means "service it whenever".
const getTimeWindow = (job: ConnectProto.Job) => {
  const start = fromTimestamp(job.twStartAt)?.getTime();
  const end = fromTimestamp(job.twEndAt)?.getTime();
  if (start == null && end == null) return undefined;
  return { start: start ?? end!, end: end ?? start! };
};

// Only an unassigned job can go on a route. A missed one is re-issued as a new
// attempt on confirm (useJobPickerModal), which needs its order number.
export const isPickable = (job: ConnectProto.Job) =>
  job.status === ConnectProto.JobStatus.CREATED ||
  (job.status === ConnectProto.JobStatus.NOT_COMPLETED && !!job.orderNumber);

/**
 * The jobs a dispatcher works on `date`: overdue first (deadline already gone), then
 * windowless "do it now" ones, then those whose window touches the day. Other days'
 * windows stay out. No date keeps every job.
 */
export const jobsForDay = (jobs: ConnectProto.Job[], date: Date | undefined) => {
  const dayStart = date ? startOfDay(date).getTime() : undefined;
  const dayEnd = date ? endOfDay(date).getTime() : undefined;
  // A future date must not count windows that simply haven't closed yet.
  const overdueCutoff = dayStart != null ? Math.min(dayStart, Date.now()) : undefined;

  const overdue: ConnectProto.Job[] = [];
  const windowless: ConnectProto.Job[] = [];
  const ofDay: ConnectProto.Job[] = [];

  jobs.forEach((job) => {
    const window = getTimeWindow(job);
    if (!window) {
      windowless.push(job);
      return;
    }
    if (dayStart == null || dayEnd == null || overdueCutoff == null) {
      ofDay.push(job);
      return;
    }
    if (window.end < overdueCutoff) {
      overdue.push(job);
      return;
    }
    if (window.start <= dayEnd && window.end >= dayStart) ofDay.push(job);
  });

  return [...overdue, ...windowless, ...ofDay];
};

/**
 * The newest driver incident of each NOT_COMPLETED job in `jobs` — it says why the job
 * was missed. `ownerOrganizationId` is required and single; none skips the query.
 */
export const useIncidentByJob = (
  ownerOrganizationId: bigint | undefined,
  jobs: ConnectProto.Job[],
) => {
  const missedJobIds = useMemo(
    () =>
      jobs
        .filter((job) => job.status === ConnectProto.JobStatus.NOT_COMPLETED)
        .map((job) => job.id),
    [jobs],
  );
  const { data, isSuccess } = useListDriverIncidentsQuery(
    { ownerOrganizationId, jobIds: missedJobIds },
    { skip: !ownerOrganizationId || missedJobIds.length === 0 },
  );
  // The newest report wins when a driver reported more than once.
  const incidentByJob = useMemo(() => {
    const byJob = new Map<bigint, ConnectProto.DriverIncident>();
    data?.driverIncidents.forEach((incident) => {
      const current = byJob.get(incident.jobId);
      if (!current || incident.id > current.id) byJob.set(incident.jobId, incident);
    });
    return byJob;
  }, [data]);
  return { incidentByJob, incidentsReady: isSuccess };
};
